import "@/index.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { registerServiceWorker } from "@/lib/push";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import AppShell from "@/components/AppShell";
import AdminShell from "@/components/AdminShell";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import ProtocolsPage from "@/pages/ProtocolsPage";
import CheckinPage from "@/pages/CheckinPage";
import ExamsPage from "@/pages/ExamsPage";
import ApplicationsPage from "@/pages/ApplicationsPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import ChatPage from "@/pages/ChatPage";
import AdminDashboardPage from "@/pages/admin/AdminDashboardPage";
import AdminPatientsPage from "@/pages/admin/AdminPatientsPage";
import AdminPatientDetailPage from "@/pages/admin/AdminPatientDetailPage";
import AdminExamsPage from "@/pages/admin/AdminExamsPage";
import AdminAlertsPage from "@/pages/admin/AdminAlertsPage";
import AdminEmailQueuePage from "@/pages/admin/AdminEmailQueuePage";
import AdminServicesPage from "@/pages/admin/AdminServicesPage";
import AdminProfessionalsPage from "@/pages/admin/AdminProfessionalsPage";
import AdminAppointmentsPage from "@/pages/admin/AdminAppointmentsPage";
import AdminProtocolsPage from "@/pages/admin/AdminProtocolsPage";
import AdminCheckinsPage from "@/pages/admin/AdminCheckinsPage";
import AdminPixPage from "@/pages/admin/AdminPixPage";
import AdminApplicationsPage from "@/pages/admin/AdminApplicationsPage";
import AdminPatientProtocolEditPage from "@/pages/admin/AdminPatientProtocolEditPage";
import AdminCouponsPage from "@/pages/admin/AdminCouponsPage";
import AdminMarkersPage from "@/pages/admin/AdminMarkersPage";
import AdminDrugsPage from "@/pages/admin/AdminDrugsPage";
import AdminClinicalPatternsPage from "@/pages/admin/AdminClinicalPatternsPage";
import AdminMetabolicTestsPage from "@/pages/admin/AdminMetabolicTestsPage";
import AdminTestsCatalogPage from "@/pages/admin/AdminTestsCatalogPage";
import AdminExamAnalysisPage from "@/pages/admin/AdminExamAnalysisPage";
import PublicMetabolicTestPage from "@/pages/PublicMetabolicTestPage";
import PublicAnamnesisPage from "@/pages/PublicAnamnesisPage";
import VivaMelhorPage from "@/pages/VivaMelhorPage";
import PublicMetabolicReportPage from "@/pages/PublicMetabolicReportPage";
import PublicTestsListPage from "@/pages/PublicTestsListPage";
import PublicDynamicTestPage from "@/pages/PublicDynamicTestPage";
import PublicTestReportPage from "@/pages/PublicTestReportPage";
import MagicLinkConsumePage from "@/pages/MagicLinkConsumePage";
import PublicAppointmentsPage from "@/pages/PublicAppointmentsPage";
import AdminAnamnesisPage from "@/pages/admin/AdminAnamnesisPage";

const patient = (El) => (
  <ProtectedRoute><AppShell><El /></AppShell></ProtectedRoute>
);
const admin = (El) => (
  <AdminRoute><AdminShell><El /></AdminShell></AdminRoute>
);

function App() {
  useEffect(() => { registerServiceWorker(); }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <PWAInstallBanner />
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/cadastro" element={<RegisterPage />} />
          <Route path="/teste/:token" element={<PublicMetabolicTestPage />} />
          <Route path="/pre-consulta/anamnese/:token" element={<PublicAnamnesisPage />} />
          <Route path="/viva-melhor" element={<VivaMelhorPage />} />
          <Route path="/relatorio-metabolico/:token" element={<PublicMetabolicReportPage />} />
          <Route path="/testes" element={<PublicTestsListPage />} />
          <Route path="/testes/:slug" element={<PublicDynamicTestPage />} />
          <Route path="/relatorio-teste/:token" element={<PublicTestReportPage />} />
          <Route path="/acesso/:token" element={<MagicLinkConsumePage />} />
          <Route path="/agendamento" element={<PublicAppointmentsPage />} />

          <Route path="/app" element={patient(DashboardPage)} />
          <Route path="/app/protocolos" element={patient(ProtocolsPage)} />
          <Route path="/app/check-in" element={patient(CheckinPage)} />
          <Route path="/app/exames" element={patient(ExamsPage)} />
          <Route path="/app/aplicacoes" element={<Navigate to="/app" replace />} />
          <Route path="/app/agendamentos" element={patient(AppointmentsPage)} />
          <Route path="/app/assinatura" element={patient(SubscriptionPage)} />
          <Route path="/app/chat" element={patient(ChatPage)} />

          <Route path="/admin" element={admin(AdminDashboardPage)} />
          <Route path="/admin/pacientes" element={admin(AdminPatientsPage)} />
          <Route path="/admin/pacientes/:id" element={admin(AdminPatientDetailPage)} />
          <Route path="/admin/exames" element={admin(AdminExamsPage)} />
          <Route path="/admin/alertas" element={admin(AdminAlertsPage)} />
          <Route path="/admin/emails" element={admin(AdminEmailQueuePage)} />
          <Route path="/admin/servicos" element={admin(AdminServicesPage)} />
          <Route path="/admin/profissionais" element={admin(AdminProfessionalsPage)} />
          <Route path="/admin/agendamentos" element={admin(AdminAppointmentsPage)} />
          <Route path="/admin/protocolos" element={admin(AdminProtocolsPage)} />
          <Route path="/admin/check-ins" element={admin(AdminCheckinsPage)} />
          <Route path="/admin/pix" element={admin(AdminPixPage)} />
          <Route path="/admin/aplicacoes" element={admin(AdminApplicationsPage)} />
          <Route path="/admin/cupons" element={admin(AdminCouponsPage)} />
          <Route path="/admin/marcadores" element={admin(AdminMarkersPage)} />
          <Route path="/admin/farmacos" element={admin(AdminDrugsPage)} />
          <Route path="/admin/padroes" element={admin(AdminClinicalPatternsPage)} />
          <Route path="/admin/testes-metabolicos" element={admin(AdminMetabolicTestsPage)} />
          <Route path="/admin/biblioteca-testes" element={admin(AdminTestsCatalogPage)} />
          <Route path="/admin/anamnese" element={admin(AdminAnamnesisPage)} />
          <Route path="/admin/exames/:examId/analisar" element={admin(AdminExamAnalysisPage)} />
          <Route path="/admin/pacientes/:id/protocolo" element={admin(AdminPatientProtocolEditPage)} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
